import { useGetChannelAnalyticsApiV1, useGetChannelAnalyticsApiV1Suspense } from "@/api/__generated__/api.schema.ts";
import type { ChannelAnalytics, StatsAggregate } from "@/api/__generated__/models";
import { LinearChart } from "@/components/dashboard/linear-chart";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { formatDateRange, getPreviousDateRange } from "@/utils/date";
import { formatCurrency } from "@/utils/format.ts";
import { stripNil } from "@/utils/strip-nil";
import { Typography } from "@backpac/monica";
import { useMemo } from "react";
import { Temporal } from "temporal-polyfill";
import { useChannelAnalytics } from "../../hooks/use-channel-analytics";
import { AnalyticsCard } from "./channel-analytics-card";
const MetricDescriptions = {
  totalViews: {
    title: "Total views",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The number of people who viewed your Shorts compared to ${formatDateRange(dateRange)}`,
  },
  premiumSubscriberViews: {
    title: "Premium subscriber's views",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The number of YouTube Premium subscribers who viewed your Shorts compared to ${formatDateRange(dateRange)}`,
  },
  estimatedPayouts: {
    title: "Estimated payouts",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The estimated earning from your Shorts, based on total views and the percentage of views from YouTube Premium subscribers compared to ${formatDateRange(dateRange)}`,
  },
  rpm: {
    title: "RPM",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The estimated earnings per 1,000 views compared to ${formatDateRange(dateRange)}`,
  },
  totalWatchTime: {
    title: "Total watch time",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The total amount of time viewers spent watching your Shorts compared to ${formatDateRange(dateRange)}`,
  },
  averageWatchTime: {
    title: "Average watch time",
    description: (dateRange: [Temporal.PlainDate, Temporal.PlainDate]) =>
      `The average duration that each viewer spent watching your YouTube Shorts compared to ${formatDateRange(dateRange)}`,
  },
} as const;

type ChannelMetric = {
  aggregated: string;
  diffRatio: number | null;
  data: {
    date: string;
    value: number;
  }[];
};

// type: "total" | "average"
// total: HH:mm:ss, 10000 hours+ -> HH:mm:ss hours
// average: mm:ss
const formatDuration = (value: number, type: "total" | "average") => {
  if (type === "total") {
    const duration = Temporal.Duration.from({
      seconds: Math.round(value),
    }).round({ largestUnit: "hour" });

    if (duration.hours >= 10000) {
      return `${duration.hours.toLocaleString()} hours`;
    }

    return new globalThis.Intl.DurationFormat("en", {
      style: "digital",
      // @ts-expect-error Intl.DurationFormat is not fully supported in TypeScript
      hours: "2-digit",
      hoursDisplay: "always",
    })
      .format(duration)
      .replace(/^:, /, ""); // NOTE: workaround Safari 18 bug;
  }

  return new globalThis.Intl.DurationFormat("en", {
    style: "digital",
    hoursDisplay: "auto",
  })
    .format(
      Temporal.Duration.from({ seconds: Math.round(value) }).round({
        largestUnit: "hour",
      }),
    )
    .replace(/^:, /, ""); // NOTE: workaround Safari 18 bug
};

function getAnalyticsMetric(
  currentAnalytics: ChannelAnalytics,
  prevAnalytics: ChannelAnalytics,
  key: keyof ChannelAnalytics["metrics"],
  aggregateKey: keyof StatsAggregate,
  formatValue: (value: number) => string,
) {
  const currentValue = currentAnalytics.metrics[key].aggregate[aggregateKey];
  const previousValue = prevAnalytics.metrics[key].aggregate[aggregateKey];

  const diffRatio = previousValue === 0 ? 0 : ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  return {
    aggregated: formatValue(currentValue),
    data: currentAnalytics.metrics[key].data.map((item, idx) => ({
      date: Temporal.PlainDate.from(currentAnalytics.from).add({ days: idx }).toString(),
      value: item,
    })),
    diffRatio,
  };
}

type ChannelAnalyticsMetricsProps = {
  dateRange: [Temporal.PlainDate, Temporal.PlainDate];
};

const _ChannelAnalyticsDashboard = ({ dateRange }: ChannelAnalyticsMetricsProps) => {
  const { channelId } = useChannelAnalytics();
  const workspaceId = useWorkspaceId();

  const {
    data: { data: channelAnalytics },
  } = useGetChannelAnalyticsApiV1Suspense(workspaceId, channelId!, {
    from: dateRange[0].toString(),
    to: dateRange[1].toString(),
  });

  const prevDateRange = getPreviousDateRange(dateRange);
  const {
    data: { data: prevChannelAnalytics },
  } = useGetChannelAnalyticsApiV1Suspense(workspaceId, channelId!, {
    from: prevDateRange[0].toString(),
    to: prevDateRange[1].toString(),
  });

  const totalViews = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    return getAnalyticsMetric(data, prevData, "totalViews", "sum", (v) => v.toLocaleString());
  }, [channelAnalytics, prevChannelAnalytics]);

  const premiumSubscribers = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    return getAnalyticsMetric(data, prevData, "premiumSubscriberViews", "sum", (v) => v.toLocaleString());
  }, [channelAnalytics, prevChannelAnalytics]);

  const estimatedPayouts = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    return getAnalyticsMetric(data, prevData, "estimatedPayouts", "sum", formatCurrency);
  }, [channelAnalytics, prevChannelAnalytics]);

  const rpm = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    return getAnalyticsMetric(data, prevData, "rpm", "mean", formatCurrency);
  }, [channelAnalytics, prevChannelAnalytics]);

  const totalWatchTime = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    return getAnalyticsMetric(data, prevData, "duration", "sum", (v) => formatDuration(v, "total"));
  }, [channelAnalytics, prevChannelAnalytics]);

  const averageWatchTime = useMemo<ChannelMetric>(() => {
    if (channelAnalytics == null) {
      return {
        aggregated: "N/A",
        data: [],
        diffRatio: null,
      };
    }
    const data = stripNil(channelAnalytics).item!;
    const prevData = stripNil(prevChannelAnalytics).item!;
    const _data = getAnalyticsMetric(data, prevData, "duration", "mean", (v) => formatDuration(v, "average"));
    _data.data = _data.data.map((item, idx) => {
      const val = item.value / data.metrics.totalViews.data[idx];
      return {
        ...item,
        value: Number.isNaN(val) ? 0 : Math.round(val),
      };
    });

    return _data;
  }, [channelAnalytics, prevChannelAnalytics]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnalyticsCard
          title={MetricDescriptions.totalViews.title}
          description={MetricDescriptions.totalViews.description(getPreviousDateRange(dateRange))}
          value={totalViews.aggregated}
          diffRatio={totalViews.diffRatio}
        >
          <LinearChart data={totalViews.data} formatValue={(value) => value.toLocaleString()} />
        </AnalyticsCard>

        <AnalyticsCard
          title={MetricDescriptions.premiumSubscriberViews.title}
          description={MetricDescriptions.premiumSubscriberViews.description(getPreviousDateRange(dateRange))}
          value={premiumSubscribers.aggregated}
          diffRatio={premiumSubscribers.diffRatio}
        >
          <LinearChart data={premiumSubscribers.data} formatValue={(value) => value.toLocaleString()} />
        </AnalyticsCard>
        <AnalyticsCard
          title={MetricDescriptions.estimatedPayouts.title}
          description={MetricDescriptions.estimatedPayouts.description(getPreviousDateRange(dateRange))}
          value={estimatedPayouts.aggregated}
          diffRatio={estimatedPayouts.diffRatio}
        >
          <LinearChart data={estimatedPayouts.data} formatValue={(v) => formatCurrency(v)} />
        </AnalyticsCard>
        <AnalyticsCard
          title={MetricDescriptions.rpm.title}
          description={MetricDescriptions.rpm.description(dateRange)}
          value={rpm.aggregated}
          diffRatio={rpm.diffRatio}
        >
          <LinearChart data={rpm.data} formatValue={(v) => formatCurrency(v)} />
        </AnalyticsCard>
        <AnalyticsCard
          title={MetricDescriptions.totalWatchTime.title}
          description={MetricDescriptions.totalWatchTime.description(getPreviousDateRange(dateRange))}
          value={totalWatchTime.aggregated}
          diffRatio={totalWatchTime.diffRatio}
        >
          <LinearChart data={totalWatchTime.data} formatValue={(value) => formatDuration(value, "total")} />
        </AnalyticsCard>
        <AnalyticsCard
          title={MetricDescriptions.averageWatchTime.title}
          description={MetricDescriptions.averageWatchTime.description(getPreviousDateRange(dateRange))}
          value={averageWatchTime.aggregated}
          diffRatio={averageWatchTime.diffRatio}
        >
          <LinearChart data={averageWatchTime.data} formatValue={(value) => formatDuration(value, "average")} />
        </AnalyticsCard>
      </div>
      <Typography size="xs" color="textMuted" className="italic">
        Views and earnings from YouTube Shorts are reported based on data from two days prior (D-2).
      </Typography>
    </div>
  );
};

_ChannelAnalyticsDashboard.Pending = () => {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      <AnalyticsCard.Pending />
      <AnalyticsCard.Pending />
      <AnalyticsCard.Pending />
      <AnalyticsCard.Pending />
      <AnalyticsCard.Pending />
      <AnalyticsCard.Pending />
    </div>
  );
};

_ChannelAnalyticsDashboard.NotAvailable = ({ dateRange }: { dateRange: [Temporal.PlainDate, Temporal.PlainDate] }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.totalViews.title}
          description={MetricDescriptions.totalViews.description(dateRange)}
        />
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.premiumSubscriberViews.title}
          description={MetricDescriptions.premiumSubscriberViews.description(dateRange)}
        />
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.estimatedPayouts.title}
          description={MetricDescriptions.estimatedPayouts.description(dateRange)}
        />
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.rpm.title}
          description={MetricDescriptions.rpm.description(dateRange)}
        />
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.totalWatchTime.title}
          description={MetricDescriptions.totalWatchTime.description(dateRange)}
        />
        <AnalyticsCard.NotAvailable
          title={MetricDescriptions.averageWatchTime.title}
          description={MetricDescriptions.averageWatchTime.description(dateRange)}
        />
      </div>
      <Typography size="xs" color="textMuted" className="italic">
        Views and earnings from YouTube Shorts are reported based on data from two days prior (D-2).
      </Typography>
    </div>
  );
};

export const ChannelAnalyticsDashboard = ({ dateRange }: ChannelAnalyticsMetricsProps) => {
  const { channelId, channels } = useChannelAnalytics();
  const workspaceId = useWorkspaceId();
  const { data, isSuccess } = useGetChannelAnalyticsApiV1(
    workspaceId,
    channelId!,
    {
      from: dateRange[0].toString(),
      to: dateRange[1].toString(),
    },
    {
      query: {
        enabled: (channels ?? []).length > 0 && channelId != null,
      },
    },
  );

  // this covers the case where the channel is not connected
  if (channels != null && channels.length === 0) {
    return <_ChannelAnalyticsDashboard.NotAvailable dateRange={getPreviousDateRange(dateRange)} />;
  }

  // this covers the case where the channel api is loading
  if (!isSuccess) {
    return <_ChannelAnalyticsDashboard.Pending />;
  }

  const channelAnalytics = data?.data;

  if (channelAnalytics == null || stripNil(channelAnalytics).item == null) {
    return <_ChannelAnalyticsDashboard.NotAvailable dateRange={getPreviousDateRange(dateRange)} />;
  }

  return <_ChannelAnalyticsDashboard dateRange={dateRange} />;
};

ChannelAnalyticsDashboard.Pending = _ChannelAnalyticsDashboard.Pending;
